/**
 * Which log each card reads, and which date it filters by.
 *
 * One table, shared by the server and the UI, because the label has to be the routing
 * rather than a description of it. Two cards can now legitimately show different
 * numbers for what looks like the same question — Invoiced Commission counts money
 * that has not been paid, Paid Commission counts money paid in a period that may not
 * be the same one — and unless the basis is on the card that reads as a bug.
 *
 * The routing test is a single question: does the metric include money that has not
 * been paid out yet? If yes it reads the invoice log, which is the only one that can
 * see an open invoice. If no it reads the statement log, which is the only one that
 * can reproduce what a past period paid and the only one that can net an adjustment.
 */

export type KpiSourceLog = 'invoice_log' | 'statement_log' | 'both';

export type KpiDateBasis =
  | 'trip_return_date'
  | 'statement_week'
  | 'invoice_created_date'
  | 'departure_month'
  | 'rolling_90_days'
  | 'rolling_12_months'
  | 'mixed';

export interface KpiBasis {
  /** Card wording as the user sees it, so a label and a heading cannot drift apart. */
  title: string;
  source: KpiSourceLog;
  dateBasis: KpiDateBasis;
  /** One sentence for the card's tooltip, after the existing explanation. */
  note?: string;
}

export const KPI_SOURCE_LABELS: Record<KpiSourceLog, string> = {
  invoice_log: 'Invoice log',
  statement_log: 'Statement log',
  both: 'Invoice + statement log',
};

export const KPI_DATE_BASIS_LABELS: Record<KpiDateBasis, string> = {
  trip_return_date: 'by trip return date',
  statement_week: 'by statement week paid',
  invoice_created_date: 'by invoice created date',
  departure_month: 'by departure month',
  rolling_90_days: 'rolling 90 days',
  rolling_12_months: 'rolling 12 months',
  mixed: 'closed by statement week, open by return date',
};

/**
 * The three commission cards are the reason this exists. They sit next to each other
 * on three different periods, so a user will subtract them, and Outstanding is not
 * Invoiced minus Paid: Invoiced counts this year's returning trips whether or not they
 * have paid, Paid counts what any statement paid this year including trips that
 * returned in an earlier one, and Outstanding counts only the open ones. Each label
 * has to say which population it describes.
 */
export const KPI_BASIS = {
  invoicedCommission: {
    title: 'Invoiced Commission',
    source: 'invoice_log',
    dateBasis: 'trip_return_date',
    note: 'Counts every non-voided invoice returning this year, paid or not.',
  },
  paidCommission: {
    title: 'Paid Commission',
    source: 'statement_log',
    dateBasis: 'statement_week',
    note:
      'Gross commission on statements that paid this year, whatever year the trip ' +
      'returned. Not Invoiced Commission minus Outstanding Commission — the three ' +
      'cards count three different populations.',
  },
  outstandingCommission: {
    title: 'Outstanding Commission',
    source: 'invoice_log',
    dateBasis: 'trip_return_date',
    note: 'Counts only open invoices returning this year, so it is not Invoiced minus Paid.',
  },
  strategicSupplierPercentage: {
    title: 'Strategic Supplier Percentage',
    source: 'invoice_log',
    dateBasis: 'rolling_90_days',
  },
  ytdProjectedSales: {
    title: 'YTD Projected Sales',
    source: 'invoice_log',
    dateBasis: 'trip_return_date',
  },
  // Tooltip copy for the two paid-sales / net-paid cards is being written in a separate
  // task, so the routing ships without a `note` on either. `KpiBasisNote` renders nothing
  // when the note is absent, so the slot stays placeholder-safe until product fills it.
  ytdPaidSales: {
    title: 'YTD Paid Sales',
    source: 'statement_log',
    dateBasis: 'statement_week',
  },
  ytdGemQualificationCommission: {
    title: 'YTD GEM Qualification Commission',
    source: 'statement_log',
    dateBasis: 'statement_week',
    note: 'Gross commission on paid statements, not the net amount paid to you.',
  },
  ytdNetPaidCommission: {
    title: 'YTD Net Paid Commission',
    source: 'statement_log',
    dateBasis: 'statement_week',
  },
  tripsTraveled: {
    title: 'Trips Traveled',
    source: 'invoice_log',
    dateBasis: 'trip_return_date',
  },
  tripsInvoiced: {
    title: 'Trips Invoiced',
    source: 'invoice_log',
    dateBasis: 'trip_return_date',
  },
  advisorActivity: {
    title: 'Advisor Activity Log',
    source: 'both',
    dateBasis: 'mixed',
    note:
      'Trip and invoice counts come from the invoice log; sales and commission come ' +
      'from the statement log.',
  },
  revenueSnapshot: {
    title: 'Revenue Snapshot',
    source: 'invoice_log',
    dateBasis: 'rolling_12_months',
  },
  invoicedByMonth: {
    title: 'Invoices by Month',
    source: 'invoice_log',
    dateBasis: 'departure_month',
  },
  // The three ranked cards deliberately carry no basis note. They read the invoice log
  // and adjustments live in a different log, so a card that mentions them is a card
  // reading a concept it cannot see. The basis label alone is enough context.
  topDestinations: {
    title: 'Top Destinations',
    source: 'invoice_log',
    dateBasis: 'rolling_12_months',
  },
  supplierInsights: {
    title: 'Supplier Insights',
    source: 'invoice_log',
    dateBasis: 'rolling_12_months',
  },
  topClients: {
    title: 'Top Clients',
    source: 'invoice_log',
    dateBasis: 'rolling_12_months',
  },
  salesCommissionChart: {
    title: 'Sales + Commission Chart',
    source: 'both',
    dateBasis: 'mixed',
    note: 'Closed money comes from the statement log; open money from the invoice log.',
  },
} as const satisfies Record<string, KpiBasis>;

export type KpiCardKey = keyof typeof KPI_BASIS;

export function kpiBasisLabel(key: KpiCardKey): string {
  const basis = KPI_BASIS[key];
  if (!basis) return '';
  return `${KPI_SOURCE_LABELS[basis.source]} · ${KPI_DATE_BASIS_LABELS[basis.dateBasis]}`;
}

export function kpiBasisNote(key: KpiCardKey): string | undefined {
  const basis: KpiBasis = KPI_BASIS[key];
  return basis.note;
}

/** Cards that read zero until the Phase 2 backfill lands, which is expected. */
export function readsStatementLog(key: KpiCardKey): boolean {
  const basis: KpiBasis = KPI_BASIS[key];
  return basis.source === 'statement_log' || basis.source === 'both';
}
